/**
 * One mapper, one closed union, one string table.
 *
 * All five contracts share `Error { code, message }`, and `message` is OPERATOR-FACING and
 * is never rendered. `mapError` turns a failed response into a `PortalError` whose only
 * user-visible field is a `copyKey` resolved by src/copy/errors.ts.
 *
 * Context is part of the key because one status means several different things: a 409 is a
 * non-error from createPortalLink, a seat-limit block from createInvite, a last-admin
 * refusal from updateMemberRole, and a duplicate-member notice — and a generic "conflict"
 * message wastes all of it.
 */

export type ErrorKind =
  | "validation" // 400, 422 — attributable to input
  | "auth" // 401 — token; handled below the query layer
  | "scope" // 403 — role/plan; a stale-role signal
  | "notfound" // 404
  | "conflict" // 409 — the most information-dense response in these contracts
  | "ratelimit" // 429 — a cooldown, not an error
  | "unavailable" // 503 — a named dependency is down
  | "network" // fetch rejected
  | "offline" // navigator.onLine === false
  | "unknown"; // other 5xx, unparseable body

export type ErrorContext =
  | "shell.me"
  | "shell.subscription"
  | "auth.signup"
  | "auth.login"
  | "auth.reset"
  | "auth.verify"
  | "account.save"
  | "account.delete"
  | "settings.save"
  | "team.members"
  | "team.invite"
  | "team.role"
  | "team.remove"
  | "policy.read"
  | "policy.write"
  | "policy.reset"
  | "keys.list"
  | "keys.create"
  | "keys.rotate"
  | "keys.revoke"
  | "billing.subscription"
  | "billing.invoices"
  | "billing.portalLink"
  | "checkout.session"
  | "checkout.confirm"
  | "usage";

export interface PortalError {
  kind: ErrorKind;
  httpStatus: number | null;
  /** Carried for copyKey selection and a metadata-only counter. NEVER rendered. */
  contractCode: string | null;
  context: ErrorContext;
  retryable: boolean;
  copyKey: string;
  /** Set when the server named an offending field (400/422) so the form can attribute it. */
  field?: string;
}

/**
 * The distinguished non-error. `createPortalLink` returns 409 for every trialing org,
 * because there is no Stripe customer yet — that is the NORMAL case. It is returned as its
 * own type so a caller physically cannot render it through the error path.
 *
 * Reaching one at all means the UI offered a control it should have hidden. That is a UI
 * defect worth counting, not a message worth showing.
 */
export interface NotAnError {
  kind: "not-an-error";
  reason: "no-stripe-customer";
  /** Metadata only: a route id, never a path with identifiers in it. */
  uiDefectSignal: true;
}

export type MappedFailure = PortalError | NotAnError;

export function isNotAnError(e: MappedFailure): e is NotAnError {
  return e.kind === "not-an-error";
}

/** The operator-facing envelope. Parsed for `code`; `message` is read and then dropped. */
interface ErrorEnvelope {
  code?: unknown;
}

/**
 * Map a failed response (or a thrown fetch error) to a PortalError.
 *
 * `body` is the already-parsed JSON envelope when there was one. The caller reads it; this
 * function never reaches the network and never sees a `message` it could accidentally
 * carry forward — the parameter type only admits `code`.
 */
export function mapError(
  input: { status: number; body?: ErrorEnvelope } | Error,
  context: ErrorContext,
): MappedFailure {
  if (input instanceof Error) {
    const offline = typeof navigator !== "undefined" && navigator.onLine === false;
    return {
      kind: offline ? "offline" : "network",
      httpStatus: null,
      contractCode: null,
      context,
      retryable: true,
      copyKey: offline ? "error.offline" : "error.network",
    };
  }

  const { status } = input;
  const code = typeof input.body?.code === "string" ? input.body.code : null;

  // The one response that is not an error at all.
  if (status === 409 && context === "billing.portalLink") {
    return { kind: "not-an-error", reason: "no-stripe-customer", uiDefectSignal: true };
  }

  const base = { httpStatus: status, contractCode: code, context } as const;

  const out = classify(status, code, context, base);
  // On a money surface the generic copy is not good enough: "We couldn't find that." leaves
  // a customer wondering whether they were charged. Swap the generic keys for variants that
  // state the negative consequence. Asserted exhaustively in copy-honesty.test.ts.
  return MONEY_CONTEXTS.includes(context) ? { ...out, copyKey: moneyVariant(out.copyKey) } : out;
}

const MONEY_VARIANTS: Readonly<Record<string, string>> = {
  "error.validation": "error.money.validation",
  "error.notFound": "error.money.notFound",
  "error.conflict": "error.money.conflict",
  "error.cooldown": "error.money.cooldown",
  "error.unavailable": "error.billing.unavailable",
};

function moneyVariant(key: string): string {
  return MONEY_VARIANTS[key] ?? key;
}

function classify(
  status: number,
  code: string | null,
  context: ErrorContext,
  base: { httpStatus: number; contractCode: string | null; context: ErrorContext },
): PortalError {
  switch (status) {
    case 400:
    case 422:
      return { ...base, kind: "validation", retryable: false, copyKey: validationKey(context, code) };

    case 401:
      // Recovery happens in the client, below the query layer, so no screen implements it.
      return { ...base, kind: "auth", retryable: false, copyKey: "error.sessionExpired" };

    case 403:
      return { ...base, kind: "scope", retryable: false, copyKey: scopeKey(context, code) };

    case 404:
      return { ...base, kind: "notfound", retryable: false, copyKey: notFoundKey(context) };

    case 409:
      return { ...base, kind: "conflict", retryable: false, copyKey: conflictKey(context, code) };

    case 429:
      // A cooldown, not an error. Never retried; the control shows a live countdown.
      return { ...base, kind: "ratelimit", retryable: false, copyKey: "error.cooldown" };

    case 503:
      return { ...base, kind: "unavailable", retryable: true, copyKey: unavailableKey(context, code) };

    default:
      return {
        ...base,
        kind: "unknown",
        retryable: status >= 500,
        copyKey: "error.unknown",
      };
  }
}

function validationKey(context: ErrorContext, code: string | null): string {
  if (context === "keys.create" || context === "keys.rotate") return "error.keys.scopeExceeded";
  if (context === "policy.write" && code === "POLICY_INVALID") return "error.policy.invalid";
  if (context === "checkout.session") return "error.checkout.invalid";
  return "error.validation";
}

function scopeKey(context: ErrorContext, code: string | null): string {
  if (code === "PLAN_REQUIRED") return "error.planRequired";
  if (context.startsWith("billing.") || context === "checkout.session") return "error.adminOnly.billing";
  if (context.startsWith("policy.")) return "error.adminOnly.policy";
  if (context.startsWith("keys.")) return "error.adminOnly.keys";
  return "error.adminOnly";
}

function notFoundKey(context: ErrorContext): string {
  // Two context exceptions, both because the generic "We couldn't find that / Go to Home"
  // would be nonsense where it lands.
  //
  //  - shell.subscription: a brand-new org has no Stripe subscription. Not an error.
  //  - usage: the endpoint IS in the merged contract and IS implemented, but neither edge
  //    routes /v1/portal/* yet, so the call 404s. That is the DEGRADED ABSENCE the usage
  //    cards already specify — and "Go to Home" on a stat card is nonsense. When the route
  //    is wired the cards populate with no portal code change.
  if (context === "shell.subscription") return "state.trial";
  if (context === "usage") return "usage.unavailable";
  return "error.notFound";
}

function conflictKey(context: ErrorContext, code: string | null): string {
  if (context === "policy.write" || context === "policy.reset") return "error.policy.versionConflict";
  if (context === "team.invite") {
    return code === "ALREADY_MEMBER" ? "error.team.alreadyMember" : "error.team.seatLimit";
  }
  if (context === "team.role") return "error.team.lastAdmin";
  if (context === "checkout.session") {
    return code === "ALREADY_SUBSCRIBED" ? "error.checkout.alreadySubscribed" : "error.checkout.inProgress";
  }
  if (context === "account.delete") return "error.account.soleAdmin";
  return "error.conflict";
}

function unavailableKey(context: ErrorContext, code: string | null): string {
  if (code === "POLICY_UNAVAILABLE" || context.startsWith("policy.")) return "error.policy.unavailable";
  if (context.startsWith("billing.") || context === "checkout.session") return "error.billing.unavailable";
  if (context.startsWith("auth.")) return "error.auth.unavailable";
  return "error.unavailable";
}

/**
 * Contexts where an error must state what did NOT happen. Asserted mechanically in
 * src/test/copy-honesty.test.ts: on a billing surface, "say what did not happen" is the
 * difference between an error and a panic, and a brand promise that only lives in prose
 * gets eroded one PR at a time.
 */
export const MONEY_CONTEXTS: readonly ErrorContext[] = [
  "billing.subscription",
  "billing.invoices",
  "billing.portalLink",
  "checkout.session",
  "checkout.confirm",
  "account.delete",
  "team.invite",
];

/** Every context, for exhaustive test enumeration. */
export const ALL_CONTEXTS: readonly ErrorContext[] = [
  "shell.me",
  "shell.subscription",
  "auth.signup",
  "auth.login",
  "auth.reset",
  "auth.verify",
  "account.save",
  "account.delete",
  "settings.save",
  "team.members",
  "team.invite",
  "team.role",
  "team.remove",
  "policy.read",
  "policy.write",
  "policy.reset",
  "keys.list",
  "keys.create",
  "keys.rotate",
  "keys.revoke",
  "billing.subscription",
  "billing.invoices",
  "billing.portalLink",
  "checkout.session",
  "checkout.confirm",
  "usage",
];

/** Every status the contracts document, for exhaustive test enumeration. */
export const ALL_STATUSES: readonly number[] = [400, 401, 403, 404, 409, 422, 429, 500, 503];
